import * as React from 'react';
import { Link } from 'gatsby';
import { storyblokEditable, StoryblokComponent } from '@storyblok/react';
import type { SbBlokData } from '@storyblok/react';

import type { FooterStoryblok } from '../../types/storyblok';
import { replaceYearPlaceholder } from '../../lib/replaceYearPlaceholder';
import { SiteContainer } from '../layout/SiteContainer';
import waveMaskSrc from '../../images/footer/wave-mask.svg';
import ornament6 from '../../images/footer/ornament-6.svg';
import ornament7 from '../../images/footer/ornament-7.svg';
import globe2 from '../../images/footer/globe-2.png';

interface FooterProps {
  blok: FooterStoryblok;
}

// Default heading shown above the social-links region when not set in CMS.
const DEFAULT_SOCIAL_HEADING = 'Connect With Us';

// Decorative imagery (wave-mask, ornaments, globe) is positioned to match Figma
// node 171:1933 (decorative layer parent). Coordinates expressed at the 1440-
// wide desktop canvas; absolute layout + the footer's overflow-hidden clip keeps
// overflow off-screen on wider viewports. Hidden below the desktop breakpoint
// (1024px) per `tokens.breakpoints.desktop` — the mobile spec carries the
// deep-blue surface only. The wave-mask is unconditionally rendered (the wave
// is the footer's responsibility per the footer-owns-callout architecture).

const FooterDecorations: React.FC = () => (
  <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
    {/*
     * Wave-mask painted as the off-white wave-cut top edge of the deep-blue
     * footer surface. Desktop-only per R5.2; mobile shows the deep-blue
     * surface without the wave cut.
     *
     * The asset is `<svg viewBox="0 -10 1440 70">` whose path matches the
     * top edge of Figma's "footer background" gradient (node 171:2690 from
     * the homepage design): a single wave with a trough on the left
     * (≈x=480, y=54) and a peak on the right (≈x=1080, y=-3). The SVG is
     * stretched to the footer's full width with `preserveAspectRatio="none"`
     * (the wave's start/end points share the same y, so horizontal stretch
     * keeps the deep-blue edges continuous at any viewport). Positioning at
     * `top: -10px, height: 70px` aligns viewBox y=0 with footer y=0, so the
     * wave's natural y-range (peak above 0 down to dip near y=54) renders
     * inside the footer's top ~60px.
     */}
    <img
      src={waveMaskSrc}
      alt=""
      aria-hidden="true"
      data-testid="footer-wave"
      className="absolute left-0 w-full pointer-events-none select-none hidden desktop:block"
      style={{ top: '-10px', height: '70px' }}
    />
    {/* Ornament 7 — bottom-left dot grid, anchored off the left edge. */}
    <img
      src={ornament7}
      alt=""
      width={351}
      height={351}
      className="absolute hidden desktop:block"
      style={{
        left: '-128px',
        top: '56px',
        width: '351px',
        height: '351px',
        transform: 'rotate(30.58deg)',
      }}
    />
    {/* Ornament 6 — top-right dot grid, anchored off the right edge. */}
    <img
      src={ornament6}
      alt=""
      width={362}
      height={362}
      className="absolute hidden desktop:block"
      style={{
        right: '-128px',
        top: '-122px',
        width: '362px',
        height: '362px',
        transform: 'scaleY(-1) rotate(136.3deg)',
      }}
    />
    {/*
     * Globe 2 — wireframe sphere, anchored to the design canvas (1440-wide)
     * rather than the viewport. The inner span is `left:0 right:0 mx-auto
     * max-w-site`, which makes it a 1440-wide centered band inside the
     * viewport-wide decoration layer. At wider viewports the canvas (and the
     * globe within it) stays centered, with the overflow past the canvas
     * right edge clipped by the footer's `overflow-hidden`. Coordinates per
     * Figma node 171:2699 ("globe illustration 2 inverted"): the globe's
     * 1051.5px bounding box sits at canvas x=764, so the 802px image (which
     * is centered inside that bbox) lands at right:-251px, top:280px. The
     * extra `scaleX(-1)` mirrors the globe horizontally per the homepage
     * review (issue #89) — combined with the existing scaleY(-1) and
     * rotation, this puts the pole/converging lines at the upper-right of
     * the visible portion, matching Figma 355:3284.
     */}
    <span aria-hidden="true" className="absolute left-0 right-0 mx-auto h-full max-w-site">
      <img
        src={globe2}
        alt=""
        width={802}
        height={802}
        data-testid="footer-globe"
        className="absolute hidden desktop:block opacity-[0.18]"
        style={{
          right: '-251px',
          top: '280px',
          width: '802px',
          height: '802px',
          transform: 'scaleX(-1) scaleY(-1) rotate(157.15deg)',
        }}
      />
    </span>
  </span>
);

export const Footer: React.FC<FooterProps> = ({ blok }) => {
  // Year is computed once per render; SSR build and client hydration share the
  // same module-graph context, so the value is identical at hydrate time.
  const currentYear = new Date().getFullYear();
  const copyright = replaceYearPlaceholder(blok.copyright, currentYear);

  const hasNav = (blok.nav_columns?.length ?? 0) > 0;
  const hasSocial = (blok.social_links?.length ?? 0) > 0;
  const hasLogo = Boolean(blok.logo?.filename);

  const hasCallout = (blok.callout?.length ?? 0) > 0;

  return (
    <footer
      {...storyblokEditable(blok as unknown as SbBlokData)}
      className="relative overflow-hidden bg-brand-deep text-neutral-white py-12"
    >
      <FooterDecorations />

      {/*
       * Optional CTA band rendered inside the deep-blue footer zone above the
       * columns. The callout consumes the same deep-blue surface (via its
       * `background=accent` mapping to bg-brand-deep) so the visual result is
       * one continuous deep-blue zone capped by the footer's wave at its top.
       * Whitelisted to `callout` only by the footer schema (R3.4).
       */}
      {hasCallout && (
        <div className="relative">
          {blok.callout!.map((entry) => (
            <StoryblokComponent key={entry._uid} blok={entry as unknown as SbBlokData} />
          ))}
        </div>
      )}

      {/*
       * Layout per Figma node 171:1911 ("main information"):
       * - Desktop (≥1024px): logo + info-area in a single row, items-start, 140px gap
       * - Mobile (<1024px):  logo centered on top, info-area below with columns
       *                       side-by-side (NOT stacked) at a tighter gap
       * Inner info-area is always flex-row so Quick Links + Connect With Us
       * stay peers at every breakpoint.
       *
       * SiteContainer.className="relative" creates the stacking context that
       * keeps the foreground content above the absolutely-positioned
       * FooterDecorations sibling, replacing the previous per-child `relative`.
       */}
      <SiteContainer className="relative">
        <div className="flex flex-col items-center gap-10 desktop:flex-row desktop:items-start desktop:gap-[140px]">
          {hasLogo && (
            <Link to="/" className="inline-flex shrink-0">
              <img
                src={blok.logo.filename ?? ''}
                alt={blok.logo.alt ?? ''}
                width={blok.logo.width ?? undefined}
                height={blok.logo.height ?? undefined}
              />
            </Link>
          )}

          {(hasNav || hasSocial) && (
            <div className="flex flex-row items-start gap-8 desktop:gap-[140px]">
              {hasNav && (
                <nav
                  aria-label="Footer"
                  className="flex flex-row items-start gap-8 desktop:gap-[140px]"
                >
                  {blok.nav_columns!.map((column) => (
                    <StoryblokComponent key={column._uid} blok={column as unknown as SbBlokData} />
                  ))}
                </nav>
              )}

              {hasSocial && (
                <div className="flex flex-col gap-7">
                  <h2 className="text-body2 font-bold text-neutral-white">
                    {blok.social_heading ?? DEFAULT_SOCIAL_HEADING}
                  </h2>
                  <ul className="flex flex-col gap-4">
                    {blok.social_links!.map((social) => (
                      <li key={social._uid}>
                        <StoryblokComponent blok={social as unknown as SbBlokData} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/*
         * Divider — gray-100 at 30% opacity per homepage review (issue #89).
         * Per Figma node 171:2701 the divider sits at canvas left=80 with a
         * fixed width of 907px, ending at canvas x=987 — just shy of where
         * the globe sphere visually starts (~canvas x≈1010, inset from the
         * globe image's left at canvas x≈889 by its transparent padding).
         * SiteContainer's right padding ends at canvas x=1360, so a desktop
         * margin-right of (1360 − 987) = 373px reproduces Figma's end
         * position. Both the divider and the globe anchor to the 1440 design
         * canvas, so the relationship holds at any viewport width.
         */}
        <hr className="my-8 border-t border-gray-100/30 desktop:mr-[373px]" />

        <p className="text-body2 text-gray-300 text-center desktop:text-left">{copyright}</p>
      </SiteContainer>
    </footer>
  );
};
